import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Package, DollarSign, CheckCircle2, Clock, Search, Download } from "lucide-react";

const STATUSES = ["Order Placed", "Pickup Scheduled", "Picked Up", "In Process", "Out for Delivery", "Delivered", "Cancelled"];
const STATUS_COLORS = {
  "Order Placed": "bg-amber-100 text-amber-800",
  "Pickup Scheduled": "bg-stone-100 text-stone-800",
  "Picked Up": "bg-blue-100 text-blue-800",
  "In Process": "bg-pink-100 text-pink-800",
  "Out for Delivery": "bg-purple-100 text-purple-800",
  "Delivered": "bg-emerald-100 text-emerald-800",
  "Cancelled": "bg-red-100 text-red-800",
};

export default function Admin() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState(null);
  const [busy, setBusy] = useState(true);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");

  useEffect(() => {
    if (!loading) {
      if (!user) navigate("/login");
      else if (user.role !== "admin") navigate("/dashboard");
    }
  }, [loading, user, navigate]);

  const load = async () => {
    try {
      const [{ data: bs }, { data: st }] = await Promise.all([
        api.get("/admin/bookings"),
        api.get("/admin/stats"),
      ]);
      setBookings(bs);
      setStats(st);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (user?.role === "admin") load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const updateStatus = async (booking_id, status) => {
    try {
      await api.patch(`/admin/bookings/${booking_id}`, { status });
      toast.success(`Status → ${status}`);
      load();
    } catch (err) {
      toast.error("Update failed");
    }
  };

  if (loading || !user || user.role !== "admin") return null;

  const filtered = bookings.filter((b) => {
    if (filter !== "all" && b.status !== filter) return false;
    if (q) {
      const s = q.toLowerCase();
      return b.user_name?.toLowerCase().includes(s) || b.user_email?.toLowerCase().includes(s) || b.booking_id?.toLowerCase().includes(s);
    }
    return true;
  });

  return (
    <div data-testid="admin-page" className="min-h-screen bg-stone-50">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 py-10">
        <div className="mb-8">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-pink-500">Admin</div>
          <h1 className="font-heading text-4xl font-black tracking-tighter text-stone-900 mt-1">Operations dashboard</h1>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Stat icon={<Package className="w-5 h-5" />} label="Total bookings" value={stats?.total ?? "—"} />
          <Stat icon={<Clock className="w-5 h-5" />} label="Active" value={stats?.active ?? "—"} />
          <Stat icon={<CheckCircle2 className="w-5 h-5" />} label="Delivered" value={stats?.delivered ?? "—"} />
          <Stat icon={<DollarSign className="w-5 h-5" />} label="Revenue" value={stats ? `$${stats.revenue}` : "—"} />
        </div>

        <Card className="p-4 rounded-2xl border border-stone-200 bg-white mb-4 flex flex-col sm:flex-row items-center gap-3">
          <div className="relative flex-1 w-full">
            <Search className="w-4 h-4 text-stone-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input
              data-testid="admin-search"
              placeholder="Search by name, email, or booking id…"
              className="pl-9 h-11 rounded-xl"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger data-testid="admin-filter" className="w-full sm:w-56 h-11 rounded-xl">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </Card>

        <Card className="rounded-2xl border border-stone-200 bg-white overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-stone-50">
                  <TableHead>Invoice</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Service</TableHead>
                  <TableHead>Pickup</TableHead>
                  <TableHead>Detergent</TableHead>
                  <TableHead>Total</TableHead>
                  <TableHead>Payment</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>PDF</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {busy ? (
                  <TableRow><TableCell colSpan={9} className="text-center py-8 text-stone-500">Loading…</TableCell></TableRow>
                ) : filtered.length === 0 ? (
                  <TableRow><TableCell colSpan={9} className="text-center py-8 text-stone-500">No bookings.</TableCell></TableRow>
                ) : (
                  filtered.map((b) => (
                    <TableRow key={b.booking_id} data-testid={`admin-row-${b.booking_id}`}>
                      <TableCell className="font-mono text-xs">
                        <div className="font-bold text-stone-900">{b.invoice_number || `#${b.booking_id.slice(-6).toUpperCase()}`}</div>
                        <div className="text-stone-400">{b.booking_id.slice(-6).toUpperCase()}</div>
                      </TableCell>
                      <TableCell>
                        <div className="font-semibold text-stone-900">{b.user_name}</div>
                        <div className="text-xs text-stone-500">{b.user_email}</div>
                      </TableCell>
                      <TableCell>
                        <div className="text-stone-900">{b.service_tier}</div>
                        {b.express && <div className="text-xs font-bold text-pink-600">Express</div>}
                      </TableCell>
                      <TableCell>
                        <div className="text-stone-900">{b.pickup_date}</div>
                        <div className="text-xs text-stone-500">{b.pickup_time_window}</div>
                      </TableCell>
                      <TableCell>{b.detergent}</TableCell>
                      <TableCell>
                        <div className="font-heading font-bold">${b.price}</div>
                        <div className="text-xs text-stone-500">tax ${(b.tax_total || 0).toFixed(2)}</div>
                      </TableCell>
                      <TableCell>
                        <Badge className="rounded-full capitalize">{b.payment_method}</Badge>
                        <div className="text-xs text-stone-500 mt-1">{b.payment_status}</div>
                      </TableCell>
                      <TableCell>
                        <Select value={b.status} onValueChange={(v) => updateStatus(b.booking_id, v)}>
                          <SelectTrigger data-testid={`status-select-${b.booking_id}`} className={`h-9 rounded-full font-bold text-xs w-44 ${STATUS_COLORS[b.status] || ""}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Button
                          data-testid={`admin-invoice-${b.booking_id}`}
                          size="sm"
                          variant="outline"
                          onClick={async () => {
                            const token = localStorage.getItem("wp_token");
                            const url = `${process.env.REACT_APP_BACKEND_URL}/api/invoices/${b.booking_id}/pdf`;
                            const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
                            const blob = await r.blob();
                            const dlUrl = URL.createObjectURL(blob);
                            const a = document.createElement("a");
                            a.href = dlUrl;
                            a.download = `${b.invoice_number || b.booking_id}.pdf`;
                            a.click();
                            URL.revokeObjectURL(dlUrl);
                          }}
                          className="rounded-full h-8 px-3 border-stone-300"
                        >
                          <Download className="w-3.5 h-3.5" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </Card>
      </div>
    </div>
  );
}

function Stat({ icon, label, value }) {
  return (
    <Card className="p-5 rounded-2xl border border-stone-200 bg-white flex items-center gap-4">
      <div className="w-11 h-11 rounded-xl bg-pink-100 text-pink-600 flex items-center justify-center">{icon}</div>
      <div>
        <div className="text-xs uppercase font-bold tracking-widest text-stone-400">{label}</div>
        <div className="font-heading text-2xl font-black text-stone-900">{value}</div>
      </div>
    </Card>
  );
}
